import { call, delay, put, select, takeLatest } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared/enum";
import type { FiCatalogResponse } from "@mapmycustomers/shared/types/entity/fieldInsight";
import Group from "@mapmycustomers/shared/types/entity/Group";
import Route from "@mapmycustomers/shared/types/entity/Route";
import Territory from "@mapmycustomers/shared/types/entity/Territory";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import ListResponse, {
  SimpleListResponse,
} from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";

import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import { getUsersOfEntireOrganization } from "@app/store/members";
import TextSearchResult from "@app/types/TextSearchResult";

import { GROUP_TO_ENTITY } from "../util/entityNavigationTargets";

import { checkEntityMentionAccess, fetchMentionCharts, searchEntityMentions } from "./actions";

const SEARCH_ENTITY_TYPES = [
  EntityType.COMPANY,
  EntityType.COMPANY_GROUP,
  EntityType.DEAL,
  EntityType.DEAL_GROUP,
  EntityType.PEOPLE_GROUP,
  EntityType.PERSON,
  EntityType.ROUTE,
  EntityType.TERRITORY,
];
const DEBOUNCE_MS = 300;
const ENTITY_CAP = 30;
const USER_CAP = 30;

const userMentionResults = (users: User[], query: string): TextSearchResult[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return [];
  }
  // One person can hold two records per username; keep the named one.
  const byUsername = new Map<string, User>();
  users.forEach((user) => {
    const key = (user.username ?? "").toLowerCase();
    const existing = byUsername.get(key);
    if (!existing || (!existing.fullName && user.fullName)) {
      byUsername.set(key, user);
    }
  });
  return [...byUsername.values()]
    .filter((user) => {
      const name = userDisplayName(user).toLowerCase();
      const email = (user.username ?? "").toLowerCase();
      return name.includes(normalizedQuery) || email.includes(normalizedQuery);
    })
    .slice(0, USER_CAP)
    .map(
      (user): TextSearchResult => ({
        entity: EntityType.USER,
        entityId: user.id,
        metaFields: "",
        name: userDisplayName(user),
        rank: 0,
      })
    );
};

function* onSearchEntityMentions({
  payload: { query },
}: ReturnType<typeof searchEntityMentions.request>) {
  let userResults: TextSearchResult[] = [];
  try {
    // takeLatest cancels this task on a newer query — aborting the in-flight
    // request via the api client's redux-saga CANCEL support — so the delay
    // also debounces and a slow earlier search can't clobber a newer one.
    yield delay(DEBOUNCE_MS);
    const users: User[] = yield select(getUsersOfEntireOrganization);
    userResults = userMentionResults(users, query);
    const orgId: Organization["id"] | undefined = yield select(getOrganizationId);
    if (!isNotEmpty(orgId)) {
      yield put(searchEntityMentions.success({ results: userResults }));
      return;
    }
    const response: SimpleListResponse<TextSearchResult> = yield callApi(
      "search",
      orgId,
      query,
      SEARCH_ENTITY_TYPES
    );
    // The server returns USER hits regardless of the filter; they come from the roster above.
    const entityResults = response.data
      .filter(({ entity }) => SEARCH_ENTITY_TYPES.includes(entity))
      .slice(0, ENTITY_CAP);
    yield put(searchEntityMentions.success({ results: [...entityResults, ...userResults] }));
  } catch (error) {
    // The entity search failed, but the roster matches are still a usable result.
    yield put(searchEntityMentions.success({ results: userResults }));
    yield put(handleError({ error }));
  }
}

function* onCheckEntityMentionAccess({
  payload: { entityId, entityType, onAllowed, onDenied },
}: ReturnType<typeof checkEntityMentionAccess>) {
  try {
    const orgId: Organization["id"] | undefined = yield select(getOrganizationId);
    if (!isNotEmpty(orgId)) {
      yield call(onDenied);
      return;
    }

    let read: boolean | undefined;
    const groupTarget = GROUP_TO_ENTITY[entityType];
    if (groupTarget) {
      const group: Group = yield callApi("fetchGroup", orgId, groupTarget, entityId, {
        includeAccessStatus: true,
      });
      read = group?.accessStatus?.read;
    } else if (entityType === EntityType.ROUTE) {
      const route: Route = yield callApi("fetchUnifiedRoute", orgId, entityId, {
        includeAccessStatus: true,
      });
      read = route?.accessStatus?.read;
    } else if (entityType === EntityType.TERRITORY) {
      const territory: Territory = yield callApi("fetchTerritory", orgId, entityId, {
        includeAccessStatus: true,
        skipShape: true,
      });
      read = territory?.accessStatus?.read;
    } else {
      yield call(onAllowed);
      return;
    }

    yield call(read === false ? onDenied : onAllowed);
  } catch {
    // A failed pre-flight (no read access or a deleted entity) must not
    // navigate — let the hook surface its no-access notification instead.
    yield call(onDenied);
  }
}

function* onFetchMentionCharts({
  payload: { organizationId },
}: ReturnType<typeof fetchMentionCharts.request>) {
  try {
    // Deliberately unscoped: omitting `scope` asks the server for the full
    // catalog, so a marker resolves whatever scope its author was reading at.
    const response: ListResponse<FiCatalogResponse> = yield callApi(
      "fetchFieldInsightsCharts",
      organizationId
    );
    yield put(fetchMentionCharts.success({ charts: response.data }));
  } catch (error) {
    yield put(fetchMentionCharts.failure());
    // Names are cosmetic — an unresolved marker shows as not available — so
    // this must not raise a toast on a surface the user asked nothing of.
    yield put(handleError({ displayNotification: false, error }));
  }
}

export function* entityMentionsSaga() {
  yield takeLatest(searchEntityMentions.request, onSearchEntityMentions);
  yield takeLatest(checkEntityMentionAccess, onCheckEntityMentionAccess);
  // takeLatest, not takeLeading: the reducer stamps the requested org on the
  // request, so an org switch mid-flight has to supersede the earlier fetch or
  // the stamp and the catalog it holds would disagree.
  yield takeLatest(fetchMentionCharts.request, onFetchMentionCharts);
}
